import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {SegmentedControl} from '@forecast-it/design-system';
import type {ActivityTab_activity$data, ActivityTab_activity$key} from './__generated__/ActivityTab_activity.graphql';
import {ActivityTimeline} from './ActivityTimeline';

// Only what the filter needs. Everything an entry renders is read by the entry's
// own fragment, so this stays a discriminator and cannot drift from what is shown.
const ActivityTabFragment = graphql`
	fragment ActivityTab_activity on ActivityEvent @relay(plural: true) {
		__typename
		... on MessageActivity {
			createdAt
		}
		... on InternalCommentActivity {
			createdAt
		}
		... on EventActivity {
			createdAt
		}
		... on AttachmentActivity {
			createdAt
		}
		...ActivityTimelineItem_activity
	}
`;

type FilterId = 'all' | 'comments' | 'status';
type Bucket = 'comments' | 'status';

/**
 * Which slice of the history an entry belongs to.
 *
 * Two buckets, because an agent reads a history one of two ways: what people said,
 * or what the system did. Emails and internal notes are both people talking, so they
 * share a bucket — splitting them would mean picking two filters to follow one
 * conversation.
 */
function bucketFor(typename: string): Bucket {
	return typename === 'MessageActivity' || typename === 'InternalCommentActivity' ? 'comments' : 'status';
}

const FILTERS: {id: FilterId; label: string}[] = [
	{id: 'all', label: 'All'},
	{id: 'comments', label: 'Comments'},
	{id: 'status', label: 'Status'},
];

const Tab = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[500]};
`;
const FilterRow = styled.div`
	display: flex;
`;
// The counts must not wrap mid-label, or a segment reads as two lines.
const SegmentLabel = styled.span`
	white-space: nowrap;
`;

interface Props {
	entries: ActivityTab_activity$key;
	/** Passed through so an inbound email can name its sender. */
	requesterEmail?: string;
}

type Entry = ActivityTab_activity$data[number];

/**
 * The whole history, newest first, filterable.
 *
 * The mock's fourth segment is an AI slice. The ticketing service writes no AI
 * activity events today — nothing in the backend emits one — so that segment would
 * be a permanently empty filter, and it is left out until there is something for it
 * to show.
 */
export function ActivityTab({entries, requesterEmail}: Props) {
	const data = useFragment<ActivityTab_activity$key>(ActivityTabFragment, entries);
	const [activeFilter, setActiveFilter] = useState<FilterId>('all');

	const concrete = useMemo(() => data.filter((e: Entry) => e.__typename !== '%other'), [data]);

	const counts = useMemo(() => {
		const result = {all: concrete.length, comments: 0, status: 0};
		for (const entry of concrete) result[bucketFor(entry.__typename)]++;
		return result;
	}, [concrete]);

	const visible = useMemo(() => {
		const filtered =
			activeFilter === 'all' ? concrete.slice() : concrete.filter((e: Entry) => bucketFor(e.__typename) === activeFilter);
		// Newest first: a history is read from what just happened backwards.
		filtered.sort((a: Entry, b: Entry) => {
			const av = new Date((a.createdAt as string) ?? 0).getTime();
			const bv = new Date((b.createdAt as string) ?? 0).getTime();
			return bv - av;
		});
		return filtered;
	}, [concrete, activeFilter]);

	const emptyText = concrete.length === 0 ? 'No activity yet on this ticket.' : `No ${activeFilter} entries on this ticket.`;

	return (
		<Tab data-testid="activity-tab">
			<FilterRow>
				<SegmentedControl
					value={activeFilter}
					onValueChange={value => setActiveFilter(value as FilterId)}
					label="Filter activity"
				>
					{FILTERS.map(filter => {
						// The count rides in `label` as well as the children: `label` is the
						// segment's accessible name, so leaving it off would hide the count
						// from assistive tech and break label-in-name.
						const withCount = `${filter.label} (${counts[filter.id]})`;
						return (
							<SegmentedControl.Item key={filter.id} value={filter.id} label={withCount}>
								<SegmentLabel>{withCount}</SegmentLabel>
							</SegmentedControl.Item>
						);
					})}
				</SegmentedControl>
			</FilterRow>
			<ActivityTimeline timeline={visible} requesterEmail={requesterEmail} emptyText={emptyText} />
		</Tab>
	);
}
